import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { bind } from '@asamco/utils';
import { animationFrameScheduler, fromEvent, interval, Subject } from 'rxjs';
import { filter, sample, takeUntil, tap } from 'rxjs/operators';

@Directive({
    selector: '[asDraggable]',
    standalone: true,
})
export class DraggableDirective implements OnInit, OnDestroy {
	/**
	 * CSS Selector within the host element
	 */
	@Input()
	draggableSelector;

	private hostOriginalPosition;

	originalOffsetX: number;
	originalOffsetY: number;
	draggableElement: HTMLElement;
	draggableOriginalPosition: any;
	draggingEnabled = false;

	private destroy$ = new Subject<void>();

	constructor(private el: ElementRef) {}

	ngOnInit() {
		this.draggableElement = this.getDraggableElement();

		fromEvent(document, 'mousemove')
			.pipe(
				filter(_ => this.draggingEnabled),
				sample(interval(0, animationFrameScheduler)),
				tap(ev => this.elementDrag(ev)),
				takeUntil(this.destroy$)
			)
			.subscribe({error: console.error});

		fromEvent(document, 'mouseup')
			.pipe(
				filter(_ => this.draggingEnabled),
				tap(ev => this.closeDragElement(ev)),
				takeUntil(this.destroy$)
			)
			.subscribe({error: console.error});

		// otherwise, move the DIV from anywhere inside the DIV:
		this.draggableElement.addEventListener('mousedown', this.dragMouseDown);
	}

	/**
	 * Gets the HTML element which can be dragged
	 */
	getDraggableElement() {
		let draggableElement;

		if (this.draggableSelector != null) {
			draggableElement = this.el.nativeElement.querySelector(this.draggableSelector);
		} else if (this.el.nativeElement.classList.contains('modal-dialog')) {
			// Default selector for draggable modals
			draggableElement = this.el.nativeElement.querySelector('.modal-header');
		} else {
			// If no draggableSelector is specified, use the element itself.
			draggableElement = this.el.nativeElement;
		}

		return draggableElement;
	}

	@bind
	dragMouseDown(e) {
		e.preventDefault();

		// Save the current positions
		this.hostOriginalPosition = this.el.nativeElement.getBoundingClientRect();
		this.draggableOriginalPosition = this.draggableElement.getBoundingClientRect();

		// Calculate mouse offset on the draggable element
		this.originalOffsetX = e.clientX - this.draggableOriginalPosition.left;
		this.originalOffsetY = e.clientY - this.draggableOriginalPosition.top;

		this.draggingEnabled = true;
	}

	@bind
	elementDrag(e) {
		e.preventDefault();

		// calculate the new cursor position:
		const endX = e.clientX - this.originalOffsetX;
		const endY = e.clientY - this.originalOffsetY;

		// Calculate the movement
		const movementX = endX - this.draggableOriginalPosition.left;
		const movementY = endY - this.draggableOriginalPosition.top;

		// set the element's new position:
		this.el.nativeElement.style.top = this.hostOriginalPosition.top + movementY + 'px';
		this.el.nativeElement.style.left = this.hostOriginalPosition.left + movementX + 'px';
		this.el.nativeElement.style.position = 'absolute';
	}

	@bind
	closeDragElement(_event) {
		// stop moving when mouse button is released:
		this.draggingEnabled = false;
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
